import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { Modal } from './Ui.jsx'
import EnquiryForm from './EnquiryForm.jsx'
import Icon from './Icon.jsx'

/**
 * The frame around the link farmers are sent.
 *
 * The request sheet lives here rather than in each page, so the bottom tab bar,
 * a hatchery card and a single batch can all open the same form with whatever
 * context they have. On a phone the tab bar is how people expect to move around
 * — it is always within thumb reach, and "Request seed" is never more than one
 * tap away wherever they have scrolled to.
 */
const RequestContext = createContext(null)

export const useRequestSeed = () => useContext(RequestContext) || (() => {})

const STEPS = [
  ['Tell us what you need', 'Quantity and the date your pond will be ready.'],
  ['The desk calls you back', 'We match a hatchery and hold the seed. No booking happens online.'],
  ['You test a sample first', '100 animals reach your pond 2 days early. Below 90% survival, you walk away.'],
  ['We deliver to the pond', 'Escorted transport, certificates in hand, OTP at handover.']
]

export default function PublicLayout () {
  const [request, setRequest] = useState(null) // null | { hatchery?, batch? }
  const [howTo, setHowTo] = useState(false)
  const location = useLocation()

  const openRequest = useCallback((context = {}) => setRequest(context), [])
  const value = useMemo(() => openRequest, [openRequest])

  const onList = location.pathname === '/hatcheries'

  return (
    <RequestContext.Provider value={value}>
      <div className="pub">
        <header className="pub-bar">
          <Link className="pub-brand" to="/hatcheries">
            <span className="glyph">🦐</span>
            <span>
              AQUA PLATFORM
              <small>Kakinada seed directory</small>
            </span>
          </Link>
          <div className="spacer" />
          <button type="button" className="f-linkbtn only-desk" onClick={() => setHowTo(true)}>How it works</button>
          <Link className="btn sm" to="/login">Staff sign in</Link>
        </header>

        <Outlet />

        <footer className="pub-foot">
          <div>
            <b>Aqua Platform</b> · shrimp seed desk for the Tuni – Kakinada – U. Kothapalli belt
          </div>
          <div>
            Seed is booked through the platform desk, not directly with the hatchery. Every lot ships
            only after a 100-animal sample passes a 24-hour pond survival test at your own pond.
          </div>
          <div>Prices are quoted by the desk on the call. Nothing on this page is a booking.</div>
        </footer>

        {/* Thumb-reach navigation: the phone pattern every farmer already knows. */}
        <nav className="f-tabs">
          <Link className={`f-tab ${onList ? 'on' : ''}`} to="/hatcheries">
            <Icon name="hatchery" size={20} />
            <span>Hatcheries</span>
          </Link>
          <button type="button" className="f-tab primary" onClick={() => openRequest({})}>
            <span className="f-tab-fab"><Icon name="enquiry" size={20} /></span>
            <span>Request seed</span>
          </button>
          <button type="button" className="f-tab" onClick={() => setHowTo(true)}>
            <Icon name="bell" size={20} />
            <span>How it works</span>
          </button>
        </nav>

        {request && (
          <Modal
            title={request.batch
              ? `Request ${request.batch.plStage} seed`
              : request.hatchery ? `Request seed from ${request.hatchery.name}` : 'Request seed'}
            onClose={() => setRequest(null)}
          >
            <EnquiryForm hatchery={request.hatchery} batch={request.batch} onClose={() => setRequest(null)} />
          </Modal>
        )}

        {howTo && (
          <Modal title="How it works" onClose={() => setHowTo(false)}>
            <ol className="pub-steps">
              {STEPS.map(([title, body]) => (
                <li key={title}><b>{title}</b>{body}</li>
              ))}
            </ol>
            <button className="btn primary block big" onClick={() => { setHowTo(false); openRequest({}) }}>
              Request seed
            </button>
          </Modal>
        )}
      </div>
    </RequestContext.Provider>
  )
}
